import { Helmet } from 'react-helmet'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { ConfigProvider } from 'antd'

import './index.css'
import { AuthWrapper } from 'admin/lib/Api'
import { ShortcutsProvider } from './lib/shortcuts'

import {
  Accounts,
  Account,
  BIReports,
  BIReportEdit,
  Certificate,
  Certificates,
  Connections,
  Connection,
  CompanyEdit,
  CompanyRegister,
  Companies,
  Companies_v2,
  EditPublicApiKey,
  EditReferenceProductItem,
  DevelopmentPlayground,
  FullscreenImage,
  GroupUpdate,
  GroupUpdateLog,
  InTray,
  Notification,
  Notifications,
  OneClickLca,
  Procore,
  ProjectPage,
  Projects,
  PublicApiKeys,
  RecordEditWrapper,
  RecordEditWrapper_v2,
  RecordsSummary,
  ReferenceDataIndex,
  ReferenceProductItems,
  SignOut,
  SignInOidc,
  SmartWaste,
  TemplateGui,
  Users,
  UserEdit
} from './pages'
import * as ROUTES from './routes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults
      // We don't have any valid use-case of needing up-to-data data without a navigation or
      // page reload.
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // A failure on initial load will still retry, it just won't try and refetch in the background if connection is lost and is restored.
      refetchOnMount: false,
      staleTime: 1000 * 30 // 30 seconds
    }
  }
})

export function App() {
  return (
    <AuthWrapper>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#22aeb5',
            colorInfo: '#22aeb5',
            colorSuccess: '#016549',
            colorWarning: '#f3a203',
            colorError: '#b20a01',
            colorText: '#000000',
            colorTextBase: '#000000',
            borderRadius: 5,
            boxShadow:
              '0 3px 6px -4px rgba(255, 255, 255, 0.12), 0 6px 16px 0 rgba(255, 255, 255, 0.08), 0 9px 28px 8px rgba(255, 255, 255, 0.05)'
          }
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ShortcutsProvider>
            <BrowserRouter>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <Routes>
                  <Route
                    path={ROUTES.SIGNIN_OIDC}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Signing In...</title>
                        </Helmet>
                        <SignInOidc />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.SIGNOUT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Signing Out</title>
                        </Helmet>
                        <SignOut />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.INTRAY}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>InTray</title>
                        </Helmet>
                        <InTray />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.RECORDS_SUMMARY.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Records Summary</title>
                        </Helmet>
                        <RecordsSummary />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.GROUP_UPDATE.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Qupid</title>
                        </Helmet>
                        <GroupUpdate />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.GROUP_UPDATE.LOG}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Qupid Log</title>
                        </Helmet>
                        <GroupUpdateLog />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.RECORD.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit</title>
                        </Helmet>
                        <RecordEditWrapper />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.RECORD_V2.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit</title>
                        </Helmet>
                        <RecordEditWrapper_v2 />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.IMAGE}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Image</title>
                        </Helmet>
                        <FullscreenImage />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.REFERENCEITEMS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Reference Product Items</title>
                        </Helmet>
                        <ReferenceProductItems />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.REFERENCEITEMS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit Reference Product Item</title>
                        </Helmet>
                        <EditReferenceProductItem />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.CERTIFICATES.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Certificates</title>
                        </Helmet>
                        <Certificates />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CERTIFICATES.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit Certificate</title>
                        </Helmet>
                        <Certificate />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.COMPANIES.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Companies</title>
                        </Helmet>
                        <Companies />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.LIST_V2}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Suppliers and Manufacturers</title>
                        </Helmet>
                        <Companies_v2 />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.NEW}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>New Company</title>
                        </Helmet>
                        <CompanyRegister />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit Company</title>
                        </Helmet>
                        <CompanyEdit />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.REFERENCE_DATA.INDEX}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Reference Data</title>
                        </Helmet>
                        <ReferenceDataIndex />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.PUBLIC_API_KEYS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Public API Keys</title>
                        </Helmet>
                        <PublicApiKeys />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PUBLIC_API_KEYS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit Public API Key</title>
                        </Helmet>
                        <EditPublicApiKey />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.TEMPLATE_GUI.INDEX}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Templates</title>
                        </Helmet>
                        <TemplateGui />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.USERS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Users</title>
                        </Helmet>
                        <Users />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.USERS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Edit User</title>
                        </Helmet>
                        <UserEdit />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.SMART_WASTE.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>SmartWaste Integration</title>
                        </Helmet>
                        <SmartWaste />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ONECLICK_LCA.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>One Click LCA Integration</title>
                        </Helmet>
                        <OneClickLca />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROCORE.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Procore Integration</title>
                        </Helmet>
                        <Procore />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.BI_REPORTS.INDEX}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>BIReports</title>
                        </Helmet>
                        <BIReports />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.BI_REPORTS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>BIReports Edit</title>
                        </Helmet>
                        <BIReportEdit />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.DEVELOPMENT.PLAYGROUND}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Playground</title>
                        </Helmet>
                        <DevelopmentPlayground />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Accounts</title>
                        </Helmet>
                        <Accounts />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Account Edit</title>
                        </Helmet>
                        <Account />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.NEW}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>New Account</title>
                        </Helmet>
                        <Account />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Projects</title>
                        </Helmet>
                        <Projects />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Project Edit</title>
                        </Helmet>
                        <ProjectPage />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.NEW}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>New Project</title>
                        </Helmet>
                        <ProjectPage />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Connections</title>
                        </Helmet>
                        <Connections />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Connection Edit</title>
                        </Helmet>
                        <Connection />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.NEW}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>New Connection</title>
                        </Helmet>
                        <Connection />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.LIST}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Notifications</title>
                        </Helmet>
                        <Notifications />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.EDIT}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>Notification Edit</title>
                        </Helmet>
                        <Notification />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.NEW}
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>New Notification</title>
                        </Helmet>
                        <Notification />
                      </>
                    }
                  />

                  <Route path="/" element={<Navigate to={ROUTES.INTRAY} />} />

                  <Route
                    path="*"
                    element={
                      <>
                        <Helmet defer={false}>
                          <title>404</title>
                        </Helmet>
                        <div>404</div>
                      </>
                    }
                  />
                </Routes>
              </QueryParamProvider>
            </BrowserRouter>
          </ShortcutsProvider>
        </QueryClientProvider>
      </ConfigProvider>
    </AuthWrapper>
  )
}
